import { motion } from "framer-motion";
import { CTAButton } from "./CTAButton";
import { useAutoplayVideo } from "@/hooks/useAutoplayVideo";

// 04 — CIERRE: every signal color drops away and the page returns to pure
// Malinois (paper / ink / accent). Clean screen, lots of air. The
// postulados ribbon that used to close this page now runs on the homepage
// instead (right above "Lo que hacemos"), so the close ends on the brand
// footage directly.
export function ServicesClosing({ closing, path }) {
  // This video sits well below the fold — see useAutoplayVideo for why a
  // plain IntersectionObserver-driven .play() isn't enough on iOS Safari
  // (it needs an actual user-gesture unlock first).
  const videoRef = useAutoplayVideo();

  return (
    <section className="relative bg-paper py-28 md:py-40" data-testid="services-closing">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="font-heading font-semibold italic tracking-tight leading-[1.15] text-3xl md:text-5xl text-ink [text-wrap:balance]"
        >
          {closing.title}
        </motion.h2>

        <div className="mt-8 space-y-3">
          {closing.paragraphs.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 }}
              className="font-body text-lg md:text-xl text-secondary leading-relaxed [text-wrap:pretty]"
            >
              {p}
            </motion.p>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0.35, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 font-heading italic text-2xl md:text-4xl text-ink leading-snug [text-wrap:balance]"
          data-testid="services-closing-statement"
        >
          {closing.statement}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
          className="mt-14 flex justify-center"
        >
          <CTAButton to={path("contact")} label={closing.cta} size="large" testId="services-closing-cta" />
        </motion.div>
      </div>

      {/* Brand footage — same full-bleed video treatment as the homepage's
          cinematic break, so the close ends on movement, not a static
          frame. */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-10% 0px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative mt-16 md:mt-20 w-full h-[50vh] md:h-[65vh] lg:h-[75vh] max-h-[700px] overflow-hidden"
        data-testid="services-closing-video"
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "grayscale(1) contrast(1.08) brightness(0.92)" }}
          src="/video/malinois-services-loop.mp4"
          poster="/video/malinois-services-loop-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
      </motion.div>
    </section>
  );
}
